import './App.css';
import React from 'react';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import {useExtractData} from './hooks/useExtractData';
import {useLocalStorage} from './hooks/useLocalStorage';
import {useConfigManager} from './hooks/useConfigManager';
import {SOURCE_PREFIX_MAP} from './constants/sources';
import {exportToCsv} from './utils/csvUtils';
import {exportToGeoJsonZip, GEOJSON_EXPORT_MODES} from './utils/geoJsonUtils';
import {useGridApiRef, gridFilteredSortedRowIdsSelector, getGridNumericOperators, getGridStringOperators} from '@mui/x-data-grid-pro';
import {numericRangeOperator} from './utils/numericRangeOperator';
import {createStringMultiSelectOperator} from './utils/stringMultiSelectOperator';

import ExtractCtrl from './svc/ExtractCtrl';
import DataToolbar from './components/DataToolbar';
import LoadingPanel from './components/LoadingPanel';
import {ProcessingPanel, TableLoadingPanel, ErrorPanel, WelcomePage} from './components/StatusPanels';
import ResultGrid from './components/ResultGrid';
import MapView from './components/MapView';
import ProcessingErrorDialog from './components/ProcessingErrorDialog';
import FavoriteIcon from '@mui/icons-material/Favorite';
import NavigationIcon from '@mui/icons-material/Navigation';
import Fab from '@mui/material/Fab';
// ── Ordre de tri des préfixes de sources (clés de SOURCE_PREFIX_MAP dans l'ordre souhaité) ──
const SOURCE_PREFIX_ORDER = Object.keys(SOURCE_PREFIX_MAP);

/**
 * Retourne l'indice de tri d'un préfixe de colonne.
 * Les préfixes inconnus sont renvoyés en fin de liste.
 */
function getPrefixSortIndex(prefix) {
    const idx = SOURCE_PREFIX_ORDER.indexOf(prefix);
    return idx === -1 ? SOURCE_PREFIX_ORDER.length : idx;
}

/**
 * Comparateur pour trier les entrées [key, meta] par préfixe de source
 * (ordre de SOURCE_PREFIX_MAP) puis par nom de colonne.
 * Garantit que les colonnes d'un même groupe sont toujours contiguës
 * → évite que MUI DataGrid Pro affiche un groupe "découpé" en plusieurs entêtes.
 */
function sortByPrefix([keyA], [keyB]) {
    const prefixA = keyA.split('.')[0];
    const prefixB = keyB.split('.')[0];
    const diff = getPrefixSortIndex(prefixA) - getPrefixSortIndex(prefixB);
    return diff !== 0 ? diff : keyA.localeCompare(keyB);
}

// ── Sérialisation stable d'un objet (clés triées) ─────────────────────────
// Évite les faux-positifs isDirty dus à un ordre d'insertion différent.
const stableStr = (obj) =>
    JSON.stringify(Object.fromEntries(Object.entries(obj ?? {}).sort(([a], [b]) => a.localeCompare(b))));

// ── Fusion de deux objets csvData { rows, meta } ─────────────────────────────
/**
 * Fusionne deux résultats csvData. Les lignes de `newData` s'ajoutent à `existing`
 * (les ids étant des batiment_groupe_id uniques, il n'y a pas de collision entre communes).
 */
function mergeCsvData(existing, newData) {
    if (!existing) return newData;
    if (!newData) return existing;
    const mergedRows = { ...existing.rows, ...newData.rows };
    const mergedMeta = { ...existing.meta };
    Object.entries(newData.meta).forEach(([key, info]) => {
        if (!mergedMeta[key]) {
            mergedMeta[key] = { type: info.type, values: [...info.values] };
        } else {
            mergedMeta[key] = {
                type: mergedMeta[key].type === 'number' && info.type === 'number' ? 'number' : 'string',
                values: [...new Set([...mergedMeta[key].values, ...info.values])].slice(0, 200),
            };
        }
    });
    return { rows: mergedRows, meta: mergedMeta };
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function App() {

    // ── Paramètres de recherche ───────────────────────────────────────────────
    const [localisation, setLocalisation] = React.useState("34274;34343;34152");
    const [typeLoc, setTypeLoc] = React.useState('commune');
    const [filter, setFilter] = React.useState({
        typesDeBiens: ['SURFACE_UTILE'], syndic: true, copro: true,
        nbLgts: 0, Dpes: ['string'], output: 'BAN_LAT_LON',
    });
    const [csvSeparator, setCsvSeparator] = useLocalStorage('extractor:csvSeparator', ';');
    const [listSeparator, setListSeparator] = useLocalStorage('extractor:listSeparator', ' | ');
    const [viewMode, setViewMode] = React.useState('table');
    const [geoMode, setGeoMode]   = React.useState(GEOJSON_EXPORT_MODES[0].key);
    const [mapViewState, setMapViewState] = React.useState(null);

    // Quand on change de mode géo, forcer le remontage de MapView via sa key
    const handleGeoModeChange = React.useCallback((newMode) => {
        setGeoMode(newMode);
    }, []);

    // ── Données et états via le hook ──────────────────────────────────────────
    const {csvData, loading, processing, processingProgress, processingError, setProcessingError, tableReady, sourcesWithState, error, cancel} =
        useExtractData(filter, geoMode);

    // ── Gestion multi-codes (localisation contenant plusieurs codes séparés par ;) ──
    const [multiCodeActive, setMultiCodeActive] = React.useState(false);
    const [mergedCsvData, setMergedCsvData]     = React.useState(null);
    const [codeProgress, setCodeProgress]       = React.useState(null); // null | {idx, total}
    const [epciResolving, setEpciResolving]     = React.useState(false);
    const pendingCodesRef  = React.useRef([]);
    const accumulatedDataRef = React.useRef(null);
    const csvDataRef       = React.useRef(null);
    const typLocRef        = React.useRef(typeLoc);
    // Type de localisation effectif utilisé pendant un cycle multi-codes.
    // Différent de typeLoc quand typeLoc === 'epci' : on résout les communes en amont
    // et on lance le workflow classique avec effectiveTypeLocRef.current === 'commune'.
    const effectiveTypeLocRef = React.useRef(typeLoc);
    csvDataRef.current     = csvData;
    typLocRef.current      = typeLoc;

    // Avancer dans la queue quand un cycle de chargement est terminé
    React.useEffect(() => {
        if (!multiCodeActive) return;
        if (loading || processing) return;
        if (!csvDataRef.current) return;

        const merged = mergeCsvData(accumulatedDataRef.current, csvDataRef.current);

        if (pendingCodesRef.current.length > 0) {
            const [next, ...rest] = pendingCodesRef.current;
            pendingCodesRef.current = rest;
            accumulatedDataRef.current = merged;
            setCodeProgress(prev => prev ? { ...prev, idx: prev.total - rest.length, code: next } : null);
            setFilter(f => ({ ...f, geo: { [effectiveTypeLocRef.current]: next } }));
        } else {
            // Tous les codes traités
            pendingCodesRef.current   = [];
            accumulatedDataRef.current = null;
            setMultiCodeActive(false);
            setCodeProgress(null);
            setMergedCsvData(merged);
        }
    }, [loading, processing, multiCodeActive]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Données effectives à afficher : fusionnées si multi-codes terminé, sinon celles du hook. */
    const effectiveCsvData = mergedCsvData ?? (multiCodeActive ? null : csvData);
    /** Loading effectif : bloqué pendant tout le processus multi-codes. */
    const effectiveLoading = loading || multiCodeActive;

    /** Annule le chargement en cours et revient à l'état initial (sans données). */
    const handleCancel = React.useCallback(() => {
        cancel();
        setMultiCodeActive(false);
        setMergedCsvData(null);
        setCodeProgress(null);
        pendingCodesRef.current = [];
        accumulatedDataRef.current = null;
        // Retirer geo du filter pour ne pas relancer automatiquement
        setFilter(f => { const { geo, ...rest } = f; return rest; });
    }, [cancel]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Construction des lignes DataGrid ──────────────────────────────────────
    const rows = React.useMemo(() => {
        if (!effectiveCsvData) return [];
        const listSep = listSeparator ?? ' | ';
        return Object.entries(effectiveCsvData.rows).map(([id, row]) => {
            const flatRow = {id};
            Object.entries(row).forEach(([k, v]) => {
                // Les points ne sont pas supportés comme noms de champ dans MUI DataGrid
                const field     = k.replace(/\./g, '__');
                const isNumeric = effectiveCsvData.meta[k]?.type === 'number';
                // Pour les colonnes numériques multivaluées, prendre la 1re valeur ; sinon dédupliquer et joindre
                const rawVal    = Array.isArray(v) ? (isNumeric ? v[0] : [...new Set(v)].join(listSep)) : v;
                // Stocker un vrai Number pour les colonnes numériques (évite les échecs de filtre MUI v8)
                flatRow[field] = (isNumeric && rawVal !== null && rawVal !== undefined && rawVal !== '')
                    ? Number(rawVal)
                    : rawVal;
            });
            return flatRow;
        });
    }, [effectiveCsvData, listSeparator]);

    // ── Construction des colonnes DataGrid ────────────────────────────────────
    // Colonnes qui ne peuvent jamais être masquées (comparaison sur le suffixe
    // car le champ est préfixé : ex. "adresse__cle_interop_adr")
    const ALWAYS_VISIBLE_SUFFIXES = new Set([
        'index',
    ]);
    const isAlwaysVisible = (field) => ALWAYS_VISIBLE_SUFFIXES.has(field.split('__').pop());

    // ── Filtre : exclure définitivement les colonnes géométriques ────────────
    // Exclure les colonnes dont la clé commence par "geom_" (préfixe entier)
    // OU dont le nom de champ (après le préfixe) commence par "geom_"
    // ex : "geom_.xxx" → "geom___xxx"  ET  "adresse.geom_cstr" → "adresse__geom_cstr"
    const isGeomKey = (key) => {
        const field = key.replace(/\./g, '__');
        return field.startsWith('geom_') || field.includes('__geom_');
    };

    const columns = React.useMemo(() => {
        if (!effectiveCsvData) return [];
        const listSep = listSeparator ?? ' | ';

        // ── Calcul des valeurs distinctes par colonne depuis les lignes courantes ──
        // (plus fiable que meta.values qui peut être figé avant agrégation)
        const fieldValues = {};
        rows.forEach(row => {
            Object.entries(row).forEach(([field, val]) => {
                if (field === 'id' || val == null || val === '') return;
                if (!fieldValues[field]) fieldValues[field] = new Set();
                const str = String(val);
                if (str.includes(listSep)) {
                    str.split(listSep).forEach(v => { const t = v.trim(); if (t) fieldValues[field].add(t); });
                } else {
                    fieldValues[field].add(str);
                }
            });
        });

        return Object.entries(effectiveCsvData.meta)
            .filter(([key]) => !isGeomKey(key))
            .filter(([key]) => !isAlwaysVisible(key.replace(/\./g, '__')))
            .sort(sortByPrefix)
            .map(([key, meta]) => {
            const [prefix] = key.split('.');
            const sourceInfo = SOURCE_PREFIX_MAP[prefix];
            const field = key.replace(/\./g, '__');
            const isNumeric = meta.type === 'number';
            const distinctValues = isNumeric ? [] : [...(fieldValues[field] ?? [])].sort();
            return {
                field,
                headerName: key,
                sourceColor: sourceInfo?.color ?? '#333',
                type: isNumeric ? 'number' : 'string',
                width: 160,
                sortable: true,
                hideable: true,
                filterOperators: isNumeric
                    ? [numericRangeOperator, ...getGridNumericOperators()]
                    : [createStringMultiSelectOperator(distinctValues, listSeparator), ...getGridStringOperators()],
                headerClassName: `col-group-${prefix}`,
            };
        });
    }, [effectiveCsvData, geoMode, rows, listSeparator]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Groupes de colonnes ───────────────────────────────────────────────────
    const columnGroupingModel = React.useMemo(() => {
        if (!effectiveCsvData) return [];
        const groups = {};
        Object.keys(effectiveCsvData.meta).filter(key => !isGeomKey(key)).filter(key => !isAlwaysVisible(key.replace(/\./g, '__'))).forEach(key => {
            const [prefix] = key.split('.');
            if (!groups[prefix]) groups[prefix] = [];
            groups[prefix].push({field: key.replace(/\./g, '__')});
        });
        return Object.entries(groups)
            .sort(([prefixA], [prefixB]) => getPrefixSortIndex(prefixA) - getPrefixSortIndex(prefixB))
            .map(([prefix, children]) => ({
            groupId: prefix,
            headerName: SOURCE_PREFIX_MAP[prefix]?.label ?? prefix,
            children,
            headerAlign: 'center',
        }));
    }, [effectiveCsvData]);

    // ── Sélection de sources (= groupes de colonnes) ──────────────────────────

    /** Une entrée par préfixe, avec la liste de ses colonnes. */
    const availableSources = React.useMemo(() => {
        if (!effectiveCsvData) return [];
        const groups = {};
        Object.entries(effectiveCsvData.meta).filter(([key]) => !isGeomKey(key)).filter(([key]) => !isAlwaysVisible(key.replace(/\./g, '__'))).forEach(([key]) => {
            const [prefix, ...rest] = key.split('.');
            const field = key.replace(/\./g, '__');
            if (!groups[prefix]) {
                groups[prefix] = {
                    key:     prefix,
                    label:   SOURCE_PREFIX_MAP[prefix]?.label ?? prefix,
                    color:   SOURCE_PREFIX_MAP[prefix]?.color ?? '#666',
                    columns: [],
                };
            }
            groups[prefix].columns.push({field, headerName: rest.join('.') || key});
        });
        return Object.entries(groups)
            .sort(([prefixA], [prefixB]) => getPrefixSortIndex(prefixA) - getPrefixSortIndex(prefixB))
            .map(([, group]) => group);
    }, [effectiveCsvData]);

    /** columnVisibilityModel est l'état de vérité pour la visibilité de chaque colonne. */
    const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage('extractor:columnVisibility', {});

    /** Alias de colonnes : { [field: string]: string } */
    const [columnAliases, setColumnAliases] = useLocalStorage('extractor:columnAliases', {});

    /** Modifie ou supprime l'alias d'un champ. */
    const handleColumnAliasChange = React.useCallback((field, alias) => {
        setColumnAliases(prev => {
            const next = { ...prev };
            if (alias) next[field] = alias;
            else delete next[field];
            return next;
        });
    }, [setColumnAliases]);

    // À chaque nouveau jeu de données : nouvelles colonnes → visible par défaut,
    // colonnes déjà connues → conserver la préférence sauvegardée.
    React.useEffect(() => {
        if (!effectiveCsvData) return;
        setColumnVisibilityModel(saved => {
            const model = {};
            Object.keys(effectiveCsvData.meta).filter(key => !isGeomKey(key)).filter(key => !isAlwaysVisible(key.replace(/\./g, '__'))).forEach(key => {
                const field = key.replace(/\./g, '__');
                model[field] = saved[field] !== undefined ? saved[field] : true;
            });
            return model;
        });
    }, [effectiveCsvData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Référence API DataGrid (pour lire les lignes filtrées) ────────────────
    const gridApiRef = useGridApiRef();

    /** Retourne un Set des IDs actuellement visibles dans le DataGrid (après filtres). */
    const getFilteredIds = () => {
        try {
            const ids = gridFilteredSortedRowIdsSelector(gridApiRef);
            return ids?.length ? new Set(ids) : null;
        } catch {
            return null;
        }
    };

    /** Retourne les clés originales (avec '.') dans l'ordre courant des colonnes de la grille. */
    const getColumnOrder = () => {
        try {
            return gridApiRef.current
                .getAllColumns()
                .map(col => col.field.replace(/__/g, '.'));
        } catch {
            return null;
        }
    };

    // ── IDs filtrés persistés (snapshot du tableau, dispo même quand carte active) ──
    const [filteredIds, setFilteredIds] = React.useState(null);

    // Réinitialiser quand les données changent (nouveau jeu → tout afficher)
    React.useEffect(() => { setFilteredIds(null); }, [rows]);

    // ── Compteur de lignes filtrées ───────────────────────────────────────────
    const [filteredRowCount, setFilteredRowCount] = React.useState(0);

    // Réinitialiser au chargement d'un nouveau jeu de données
    React.useEffect(() => { setFilteredRowCount(rows.length); }, [rows]);

    // ── Modèle de filtres contrôlé (approche MUI multi-filters) ──────────────
    // https://mui.com/x/react-data-grid/filtering/multi-filters/
    const [filterModel, setFilterModel] = React.useState({ items: [], logicOperator: 'and' });

    // Réinitialiser quand un nouveau jeu de données est chargé OU quand le mode d'agrégation change
    // (les éléments de filtrage — valeurs disponibles par colonne — sont recalculés pour le nouveau mode)
    React.useEffect(() => {
        setFilterModel({ items: [], logicOperator: 'and' });
    }, [effectiveCsvData, geoMode]);

    const handleFilterModelChange = React.useCallback((newModel) => {
        setFilterModel(newModel);
        // Revenir à la page 1 pour que les résultats filtrés soient visibles
        try { gridApiRef.current.setPage(0); } catch { /* grille pas encore prête */ }
        setTimeout(() => {
            try {
                const ids = gridFilteredSortedRowIdsSelector(gridApiRef);
                const count = ids?.length ?? rows.length;
                setFilteredRowCount(count);
                setFilteredIds(ids?.length ? new Set(ids) : null);
            } catch {
                setFilteredRowCount(rows.length);
            }
        }, 0);
    }, [rows.length, gridApiRef]);

    // ── Gestion des configurations nommées ───────────────────────────────────
    const { configList, lastConfigName, setLastConfigName, saveConfig, getConfig, deleteConfig } = useConfigManager();
    const [currentConfigName, setCurrentConfigName] = React.useState(null);

    /**
     * Snapshot des paramètres au moment du dernier chargement/sauvegarde.
     * isDirty = (état courant ≠ snapshot).
     */
    const [savedSnapshot, setSavedSnapshot] = React.useState(null);

    // ── Suivi réactif de l'ordre des colonnes ─────────────────────────────────
    // null = pas encore capturé (ex. pendant la restauration d'une config)
    const [columnOrder, setColumnOrder] = React.useState(null);

    // ── Ordre de colonnes à appliquer au prochain montage de la grille ────────
    // Mémorise l'ordre cible de la config courante pour le restaurer dès que
    // tableReady passe à true (notamment après un rechargement de données).
    const [pendingColumnOrder, setPendingColumnOrder] = React.useState(null);

    const handleColumnOrderChange = React.useCallback(() => {
        try {
            const order = gridApiRef.current.getAllColumns().map(c => c.field);
            setColumnOrder(order);
        } catch { /* grille pas encore prête */ }
    }, [gridApiRef]);

    /** Crée un snapshot des paramètres trackés depuis un objet config. */
    const makeSnapshot = React.useCallback((config) => ({
        typeLoc:          config.typeLoc          ?? 'commune',
        csvSeparator:     config.csvSeparator     ?? ';',
        listSeparator:    config.listSeparator    ?? ' | ',
        geoMode:          config.geoMode          ?? '',
        columnAliases:    stableStr(config.columnAliases),
        columnVisibility: stableStr(config.columnVisibilityModel),
        columnOrder:      config.columnOrder?.length ? JSON.stringify(config.columnOrder) : null,
    }), []);

    /** Vrai si l'état courant diffère du snapshot sauvegardé (pure comparaison).
     *  La localisation est exclue : changer le code de recherche n'est pas
     *  considéré comme une modification de configuration. */
    const isDirty = React.useMemo(() => {
        if (!currentConfigName || !savedSnapshot) return false;
        return (
            typeLoc       !== savedSnapshot.typeLoc       ||
            csvSeparator  !== savedSnapshot.csvSeparator  ||
            listSeparator !== savedSnapshot.listSeparator ||
            geoMode       !== savedSnapshot.geoMode       ||
            stableStr(columnAliases)          !== savedSnapshot.columnAliases    ||
            stableStr(columnVisibilityModel)  !== savedSnapshot.columnVisibility ||
            (columnOrder !== null && savedSnapshot.columnOrder !== null &&
             JSON.stringify(columnOrder) !== savedSnapshot.columnOrder)
        );
    }, [currentConfigName, savedSnapshot, typeLoc, csvSeparator, listSeparator,
        geoMode, columnAliases, columnVisibilityModel, columnOrder]);

    /** Applique les valeurs d'une config aux états React (sans effet de bord sur isDirty). */
    const applyConfig = React.useCallback((config) => {
        if (config.typeLoc               !== undefined) setTypeLoc(config.typeLoc);
        if (config.geoMode               !== undefined) setGeoMode(config.geoMode);
        if (config.csvSeparator          !== undefined) setCsvSeparator(config.csvSeparator);
        if (config.listSeparator         !== undefined) setListSeparator(config.listSeparator);
        if (config.columnVisibilityModel !== undefined) setColumnVisibilityModel(config.columnVisibilityModel);
        if (config.columnAliases         !== undefined) setColumnAliases(config.columnAliases);
        else setColumnAliases({});
    }, [setCsvSeparator, setListSeparator, setColumnVisibilityModel, setColumnAliases]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Restaure l'ordre des colonnes dans la grille (après un tick pour que la grille soit prête). */
    const restoreColumnOrder = React.useCallback((columnOrder) => {
        if (!columnOrder?.length) return;
        setTimeout(() => {
            try {
                columnOrder.forEach((field, idx) => {
                    gridApiRef.current.setColumnIndex(field, idx);
                });
            } catch { /* grille pas encore prête */ }
        }, 150);
    }, [gridApiRef]);

    // ── Restauration de l'ordre dès que la grille est prête ──────────────────
    // pendingColumnOrder est mis à jour à chaque chargement de config.
    // Quand tableReady passe à true (nouveau jeu de données), l'ordre est appliqué.
    React.useEffect(() => {
        if (!tableReady || !pendingColumnOrder?.length) return;
        restoreColumnOrder(pendingColumnOrder);
    }, [tableReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Auto-chargement de la dernière configuration au démarrage ────────────
    React.useEffect(() => {
        if (!lastConfigName) return;
        const config = getConfig(lastConfigName);
        if (!config) return;
        setColumnOrder(null);
        applyConfig(config);
        setPendingColumnOrder(config.columnOrder ?? null);
        restoreColumnOrder(config.columnOrder);
        setCurrentConfigName(lastConfigName);
        setSavedSnapshot(makeSnapshot(config));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** Construit l'objet de configuration courant. */
    const buildConfigData = React.useCallback(() => {
        let columnOrder = null;
        try { columnOrder = gridApiRef.current.getAllColumns().map(c => c.field); } catch { /* ok */ }
        return { typeLoc, csvSeparator, listSeparator, geoMode, columnVisibilityModel, columnAliases, columnOrder };
    }, [typeLoc, csvSeparator, listSeparator, geoMode, columnVisibilityModel, columnAliases, gridApiRef]);

    const [autoSaveSnack, setAutoSaveSnack] = React.useState(false);


    /** Télécharge la config sous forme de fichier JSON. */
    const downloadConfigFile = (name, data) => {
        const blob = new Blob([JSON.stringify({ name, ...data }, null, 2)], { type: 'application/json' });
        const url  = URL.createObjectURL(blob);
        const a    = Object.assign(document.createElement('a'), {
            href: url,
            download: `configuration-extractor-${name.replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().slice(0, 10)}.json`,
        });
        a.click();
        URL.revokeObjectURL(url);
    };

    /** Enregistre la config avec un nom (localStorage + téléchargement fichier). */
    const handleSaveAs = React.useCallback((name) => {
        const data = buildConfigData();
        saveConfig(name, data);
        setCurrentConfigName(name);
        setLastConfigName(name);
        setColumnOrder(null);   // reset : le snapshot suivant capturera l'ordre courant
        setSavedSnapshot(makeSnapshot(data));
        downloadConfigFile(name, data);
        setAutoSaveSnack(true);
    }, [buildConfigData, saveConfig, setLastConfigName, makeSnapshot]);

    /** Enregistre la config courante (même nom). */
    const handleSave = React.useCallback(() => {
        if (!currentConfigName) return;
        const data = buildConfigData();
        saveConfig(currentConfigName, data);
        setSavedSnapshot(makeSnapshot(data));
        downloadConfigFile(currentConfigName, data);
        setAutoSaveSnack(true);
    }, [currentConfigName, buildConfigData, saveConfig, makeSnapshot]);

    /** Charge une configuration depuis le localStorage. */
    const handleLoadConfig = React.useCallback((name) => {
        const config = getConfig(name);
        if (!config) return;
        setColumnOrder(null);
        applyConfig(config);
        setPendingColumnOrder(config.columnOrder ?? null);
        restoreColumnOrder(config.columnOrder);
        setCurrentConfigName(name);
        setLastConfigName(name);
        setSavedSnapshot(makeSnapshot(config));
    }, [getConfig, applyConfig, restoreColumnOrder, setLastConfigName, makeSnapshot]);

    /** Charge une configuration depuis un fichier JSON (et l'ajoute au localStorage). */
    const handleOpen = React.useCallback((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const config = JSON.parse(e.target.result);
                const name   = config.name || file.name.replace(/\.json$/i, '');
                setColumnOrder(null);
                applyConfig(config);
                setPendingColumnOrder(config.columnOrder ?? null);
                restoreColumnOrder(config.columnOrder);
                saveConfig(name, config);
                setCurrentConfigName(name);
                setSavedSnapshot(makeSnapshot(config));
            } catch (err) {
                console.error('Erreur lors du chargement de la configuration :', err);
            }
        };
        reader.readAsText(file);
    }, [applyConfig, restoreColumnOrder, saveConfig, makeSnapshot]);

    /** Décharge la configuration courante (réinitialise l'indicateur sans supprimer la config). */
    const handleUnloadConfig = React.useCallback(() => {
        setCurrentConfigName(null);
        setSavedSnapshot(null);
        setLastConfigName(null);
        setPendingColumnOrder(null);
    }, [setLastConfigName]);

    // ── Basculement de vue ────────────────────────────────────────────────────
    const handleViewModeChange = React.useCallback((newMode) => {
        if (newMode === 'map') {
            // Capturer les IDs filtrés au moment du basculement
            const ids = getFilteredIds();
            setFilteredIds(ids);
            // Réinitialiser le viewport pour auto-fitter les données filtrées
            setMapViewState(null);
        }
        setViewMode(newMode);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Quand on revient en vue tableau, resynchroniser le compteur de lignes filtrées
    // (le DataGrid restait monté mais caché, il faut relire son état)
    React.useEffect(() => {
        if (viewMode === 'table' && tableReady) {
            setTimeout(() => {
                try {
                    const ids = gridFilteredSortedRowIdsSelector(gridApiRef);
                    const count = ids?.length ?? rows.length;
                    setFilteredRowCount(count);
                    setFilteredIds(ids?.length ? new Set(ids) : null);
                } catch { /* DataGrid pas encore prêt */ }
            }, 50);
        }
    }, [viewMode, tableReady]); // eslint-disable-line react-hooks/exhaustive-deps


    return (
        <Box sx={{width: '100%', minHeight: '100vh', bgcolor: '#f5f6fa'}}>

            <DataToolbar
                localisation={localisation} setLocalisation={setLocalisation}
                typeLoc={typeLoc} setTypeLoc={setTypeLoc}
                loading={effectiveLoading} csvData={effectiveCsvData}
                csvSeparator={csvSeparator} setCsvSeparator={setCsvSeparator}
                listSeparator={listSeparator} setListSeparator={setListSeparator}
                onLoad={async () => {
                    setMergedCsvData(null);
                    setCodeProgress(null);
                    const inputCodes = localisation.split(';').map(c => c.trim()).filter(Boolean);
                    if (inputCodes.length === 0) return;

                    let codes;
                    if (typeLoc === 'epci') {
                        // Résoudre chaque code EPCI en liste de communes avant de lancer le workflow
                        setEpciResolving(true);
                        try {
                            const communesByEpci = await Promise.all(
                                inputCodes.map(epciCode => new Promise((resolve, reject) => {
                                    ExtractCtrl.getListOfCommuneByEpci(epciCode, null, null, resolve, reject);
                                }))
                            );
                            codes = communesByEpci.flat().filter(Boolean);
                            effectiveTypeLocRef.current = 'commune';
                        } catch (err) {
                            console.error('Erreur lors de la récupération des communes par EPCI :', err);
                            setEpciResolving(false);
                            return;
                        } finally {
                            setEpciResolving(false);
                        }
                    } else {
                        codes = inputCodes;
                        effectiveTypeLocRef.current = typeLoc;
                    }

                    if (codes.length === 0) return;
                    if (codes.length === 1) {
                        setMultiCodeActive(false);
                        pendingCodesRef.current = [];
                        setFilter({...filter, geo: {[effectiveTypeLocRef.current]: codes[0]}});
                    } else {
                        pendingCodesRef.current = codes.slice(1);
                        accumulatedDataRef.current = null;
                        setMultiCodeActive(true);
                        setCodeProgress({idx: 1, total: codes.length, code: codes[0]});
                        setFilter({...filter, geo: {[effectiveTypeLocRef.current]: codes[0]}});
                    }
                }}
                onExport={() => exportToCsv(effectiveCsvData, columnVisibilityModel, csvSeparator, getFilteredIds(), listSeparator, getColumnOrder(), columnAliases)}
                onExportGeoJson={(modeKey) => exportToGeoJsonZip(effectiveCsvData, columnVisibilityModel, modeKey, getFilteredIds())}
                onCancel={handleCancel}
                onSaveAs={handleSaveAs}
                onSave={handleSave}
                onOpen={handleOpen}
                onLoadConfig={handleLoadConfig}
                onDeleteConfig={deleteConfig}
                onUnloadConfig={handleUnloadConfig}
                savedConfigs={configList}
                currentConfigName={currentConfigName}
                isDirty={isDirty}
                availableSources={availableSources}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                rowCount={rows.length}
                filteredRowCount={filteredRowCount}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                geoMode={geoMode}
                onGeoModeChange={handleGeoModeChange}
                codeProgress={codeProgress}
                sourcesWithState={sourcesWithState}
            />

            {(loading || multiCodeActive || epciResolving) && <LoadingPanel sources={sourcesWithState} codeProgress={codeProgress}/>}

            {error && <ErrorPanel error={error}/>}

            {processing && <ProcessingPanel progress={processingProgress}/>}
            {!tableReady && !loading && !processing && effectiveCsvData && <TableLoadingPanel/>}

            {/* Page de démarrage quand aucune donnée n'est chargée */}
            {!effectiveLoading && !processing && !effectiveCsvData && !error && <WelcomePage />}

            {/* ResultGrid reste monté (CSS display) pour ne pas perdre l'état du DataGrid
                lors des allers-retours carte ↔ tableau (filtres, tri, pagination…) */}
            {tableReady && (
                <Box sx={{ display: viewMode === 'table' ? undefined : 'none' }}>
                    <ResultGrid
                        rows={rows}
                        columns={columns}
                        columnGroupingModel={[]}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={setColumnVisibilityModel}
                        apiRef={gridApiRef}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        columnAliases={columnAliases}
                        onColumnAliasChange={handleColumnAliasChange}
                        onColumnOrderChange={handleColumnOrderChange}
                    />
                </Box>
            )}

            {tableReady && viewMode === 'map' && (
                <MapView
                    key={geoMode}
                    csvData={effectiveCsvData}
                    columnVisibilityModel={columnVisibilityModel}
                    filteredIds={filteredIds}
                    geoMode={geoMode}
                    savedViewState={mapViewState}
                    onViewStateChange={setMapViewState}
                />
            )}

            <ProcessingErrorDialog
                error={processingError}
                onClose={() => setProcessingError(null)}
            />

            <Snackbar
                open={autoSaveSnack}
                autoHideDuration={2500}
                onClose={() => setAutoSaveSnack(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setAutoSaveSnack(false)}
                    severity="success"
                    variant="filled"
                    sx={{ fontSize: '0.78rem', alignItems: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}
                >
                    Configuration&nbsp;<strong>« {currentConfigName} »</strong>&nbsp;mise à jour
                </Alert>
            </Snackbar>
        </Box>
    );
}
